// Static content for the Legal Writing Academy style guides.
// Used by /education/writing and /education/writing/$style.

export type CitationStyleSlug = "oscola" | "harvard" | "apa-7" | "sa-citation";

export type CitationExample = {
  label: string;
  source: string;
  citation: string;
  notes?: string;
};

export type CitationStyle = {
  slug: CitationStyleSlug;
  name: string;
  shortName: string;
  audience: string;
  summary: string;
  whenToUse: string[];
  rules: { heading: string; body: string }[];
  examples: CitationExample[];
  pitfalls: string[];
};

export const CITATION_STYLES: CitationStyle[] = [
  {
    slug: "oscola",
    name: "Oxford University Standard for the Citation of Legal Authorities (OSCOLA)",
    shortName: "OSCOLA",
    audience: "LLB students, postgraduate law students, common-law researchers.",
    summary:
      "OSCOLA is a footnote-based citation style developed at Oxford for legal scholarship. Every authority is cited in a numbered footnote; the bibliography lists primary and secondary sources separately.",
    whenToUse: [
      "Final-year LLB essays and dissertations",
      "Submissions to law journals that adopt OSCOLA",
      "Comparative or international-law writing where a footnote style is preferred",
    ],
    rules: [
      {
        heading: "Footnotes, not in-text references",
        body: "All citations sit in footnotes — never in brackets in the body. End the footnote with a full stop.",
      },
      {
        heading: "No full stops in abbreviations",
        body: "Use 'CC', 'SCA', 'HL', 'UKSC' — not 'C.C.' or 'U.K.S.C.'.",
      },
      {
        heading: "Pinpoint references",
        body: "After the citation, give the paragraph (in square brackets) or page number — e.g. [42] for paragraph 42.",
      },
      {
        heading: "Subsequent citations",
        body: "Use 'ibid' for the immediately preceding source and a short form (party name + footnote number) for repeats.",
      },
    ],
    examples: [
      {
        label: "South African case",
        source: "Carmichele v Minister of Safety and Security, Constitutional Court, 2001, vol 4 SA 938, paragraph 44",
        citation:
          "Carmichele v Minister of Safety and Security 2001 (4) SA 938 (CC) [44].",
      },
      {
        label: "Statute",
        source: "Labour Relations Act 66 of 1995, section 191",
        citation: "Labour Relations Act 66 of 1995, s 191.",
      },
      {
        label: "Journal article",
        source:
          "Cathi Albertyn, 'Substantive equality and transformation in South Africa', SAJHR vol 23 (2007), page 253",
        citation:
          "Cathi Albertyn, 'Substantive equality and transformation in South Africa' (2007) 23 SAJHR 253.",
      },
      {
        label: "Book",
        source:
          "Iain Currie & Johan de Waal, The Bill of Rights Handbook, 6th edition, Juta, 2013",
        citation:
          "Iain Currie and Johan de Waal, The Bill of Rights Handbook (6th edn, Juta 2013).",
      },
    ],
    pitfalls: [
      "Do not italicise statute names — only case names are italicised.",
      "Pinpoint paragraph numbers go in square brackets [ ], page numbers do not.",
      "'ibid' refers only to the immediately preceding footnote.",
    ],
  },
  {
    slug: "harvard",
    name: "Harvard Author-Date Referencing",
    shortName: "Harvard",
    audience:
      "Socio-legal, criminology and interdisciplinary writing where an in-text author-date style is preferred.",
    summary:
      "Harvard places a short (Author Year) parenthetical in the body and a full alphabetical reference list at the end. Page numbers are added for direct quotes.",
    whenToUse: [
      "Socio-legal research essays",
      "Empirical legal-studies projects",
      "Courses that expressly require Harvard referencing",
    ],
    rules: [
      {
        heading: "In-text format",
        body: "(Author Year) for a paraphrase; (Author Year, p. X) for a direct quote or specific point.",
      },
      {
        heading: "Multiple authors",
        body: "Two authors: (Smith and Jones 2020). Three or more: (Smith et al. 2020).",
      },
      {
        heading: "Reference list",
        body: "Alphabetical by surname. Hanging indent. Year in parentheses after the author.",
      },
    ],
    examples: [
      {
        label: "Journal article",
        source:
          "Cathi Albertyn, 'Substantive equality and transformation in South Africa', SAJHR vol 23 (2007), page 253",
        citation:
          "Albertyn, C. (2007) 'Substantive equality and transformation in South Africa', South African Journal on Human Rights, 23(2), pp. 253-276.",
      },
      {
        label: "Book",
        source:
          "Iain Currie & Johan de Waal, The Bill of Rights Handbook, 6th edition, Juta, 2013",
        citation:
          "Currie, I. and de Waal, J. (2013) The Bill of Rights Handbook. 6th edn. Cape Town: Juta.",
      },
      {
        label: "Case (Harvard treats cases as legal sources)",
        source: "Carmichele v Minister of Safety and Security 2001 (4) SA 938 (CC)",
        citation:
          "Carmichele v Minister of Safety and Security 2001 (4) SA 938 (CC).",
        notes:
          "Cite cases in standard South African case-citation form even in a Harvard reference list.",
      },
    ],
    pitfalls: [
      "Don't shorten 'et al.' for two authors — only three or more.",
      "Page numbers belong in the in-text citation for direct quotes, not in the reference list entry for a whole book.",
      "Harvard has many institutional variants — check your faculty's house style.",
    ],
  },
  {
    slug: "apa-7",
    name: "APA 7th Edition",
    shortName: "APA 7",
    audience: "Psychology, criminology, forensic-science and interdisciplinary law work.",
    summary:
      "APA 7 uses (Author, Year) in text and a 'References' list at the end, alphabetised by surname with hanging indents. Legal sources follow a Bluebook-aligned format.",
    whenToUse: [
      "Modules taught in faculties that use APA (psychology, criminology, social work).",
      "Inter-disciplinary projects combining legal and empirical sources.",
    ],
    rules: [
      {
        heading: "In-text",
        body: "(Surname, Year) for a paraphrase; (Surname, Year, p. X) for a quote.",
      },
      {
        heading: "Three or more authors",
        body: "Use the first author followed by 'et al.' in the very first citation.",
      },
      {
        heading: "DOIs",
        body: "Include DOIs as full URLs (https://doi.org/...) at the end of journal references.",
      },
    ],
    examples: [
      {
        label: "Journal article",
        source:
          "Cathi Albertyn, 'Substantive equality and transformation in South Africa', SAJHR vol 23 (2007), page 253",
        citation:
          "Albertyn, C. (2007). Substantive equality and transformation in South Africa. South African Journal on Human Rights, 23(2), 253-276.",
      },
      {
        label: "Book",
        source:
          "Iain Currie & Johan de Waal, The Bill of Rights Handbook, 6th edition, Juta, 2013",
        citation:
          "Currie, I., & de Waal, J. (2013). The Bill of Rights handbook (6th ed.). Juta.",
      },
      {
        label: "Statute",
        source: "Labour Relations Act 66 of 1995",
        citation: "Labour Relations Act 66 of 1995 (S. Afr.).",
      },
    ],
    pitfalls: [
      "Use '&' inside parentheses and the word 'and' in narrative text.",
      "Capitalise only the first word of a book/article title (and proper nouns).",
      "Don't omit the issue number — '23(2)' is correct.",
    ],
  },
  {
    slug: "sa-citation",
    name: "South African Legal Citation",
    shortName: "SA Legal Citation",
    audience: "Practitioners and courts; widely accepted in South African moots and journals.",
    summary:
      "The conventional South African citation style used in practice and most local law journals (SALJ, THRHR, De Jure). Combines a footnote model with established local conventions for cases, legislation and government publications.",
    whenToUse: [
      "Heads of argument and practical legal writing in South African courts.",
      "Submissions to South African law journals.",
      "UNISA LLB assignments unless OSCOLA is explicitly required.",
    ],
    rules: [
      {
        heading: "Cases",
        body: "Party names in italics, then the year in round brackets, volume, law report series, first page, and court abbreviation in round brackets — e.g. (CC), (SCA), (GP).",
      },
      {
        heading: "Statutes",
        body: "'Act name Number of Year' — capitalise the word 'Act'. Cite a section as 's 23' (not 'sec. 23').",
      },
      {
        heading: "Government Gazettes",
        body: "Notice/Proclamation number in Government Notice (GN), gazette number, and publication date.",
      },
      {
        heading: "Pinpoints",
        body: "Use paragraph numbers in square brackets for newer judgments (post-2000) or page references for older reports.",
      },
    ],
    examples: [
      {
        label: "Constitutional Court case",
        source:
          "Carmichele v Minister of Safety and Security, Constitutional Court, 2001, vol 4 SA 938, paragraph 44",
        citation:
          "Carmichele v Minister of Safety and Security 2001 (4) SA 938 (CC) para 44.",
      },
      {
        label: "Statute",
        source: "Labour Relations Act 66 of 1995, section 191(5)(a)",
        citation: "Labour Relations Act 66 of 1995 s 191(5)(a).",
      },
      {
        label: "Government Notice",
        source:
          "Code of Good Practice: Dismissal, Government Notice 1631, Government Gazette 19967 of 4 June 1999",
        citation:
          "Code of Good Practice: Dismissal GN 1631 in GG 19967 of 4 June 1999.",
      },
      {
        label: "Journal article",
        source:
          "Cathi Albertyn, 'Substantive equality and transformation in South Africa', SAJHR vol 23 (2007), page 253",
        citation:
          "C Albertyn 'Substantive equality and transformation in South Africa' (2007) 23 SAJHR 253.",
      },
    ],
    pitfalls: [
      "Don't anglicise 'paragraph' as '[44]' for older pre-2000 judgments — those use page numbers.",
      "Italicise party names; do NOT italicise 'v'.",
      "Always check the latest consolidated text on www.gov.za — many older Acts have been amended.",
    ],
  },
];

export function getCitationStyle(slug: string): CitationStyle | undefined {
  return CITATION_STYLES.find((s) => s.slug === slug);
}
